import { uiText, useUiLanguage } from '../../../utils/uiTranslation';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AppLoading from '../../AppLoading';
import nationalizationService, {
  NATIONALIZATION_WORKFORCE_CHANGED_EVENT
} from '../../../services/nationalizationService';
import NationalizationCalculationChanges from './NationalizationCalculationChanges';
import NationalizationBreakdown from './NationalizationBreakdown';
import NationalizationDataQuality from './NationalizationDataQuality';
import NationalizationForecastCard from './NationalizationForecastCard';
import NationalizationOverviewCard from './NationalizationOverviewCard';
import NationalizationRecommendations from './NationalizationRecommendations';
import NationalizationReconciliation from './NationalizationReconciliation';
import NationalizationRuleSources from './NationalizationRuleSources';
import NationalizationRuleUpdates from './NationalizationRuleUpdates';
import NationalizationSetupForm from './NationalizationSetupForm';
import { isProfileReady, localizedValue } from './nationalizationViewModel';
import './Nationalization.css';

const EMPTY_PROFILE_ENVELOPE = { profile: null, missing_fields: [] };

const getErrorMessage = (error, t) => {
  if (error?.status === 401) return t('errors.sessionExpired', 'Your session expired. Sign in and try again.');
  if (error?.status === 403) return t('errors.forbidden', 'You do not have permission to view nationalization information.');
  if (error?.status === 404) return t('errors.serviceNotEnabled', 'Nationalization is not enabled in this environment yet.');
  if (error?.status === 409) return t('errors.setupIncomplete', 'Complete the required nationalization setup before continuing.');
  if (error?.status === 400 || error?.status === 422) return t('errors.invalidRequest', 'Some submitted information is invalid. Review it and try again.');
  if (error?.status >= 500) return t('errors.server', 'The nationalization service is temporarily unavailable. Please try again.');
  if (!error?.status) return t('errors.network', 'The request could not be completed. Check your connection and try again.');
  return t('errors.loadFailed', 'Nationalization information could not be loaded.');
};

export default function NationalizationView({
  companyUID,
  language = 'en',
  canManage = false,
  onOpenImport
}) {
  useUiLanguage();
  const { t } = useTranslation('nationalization');
  const [countries, setCountries] = useState([]);
  const [profileEnvelope, setProfileEnvelope] = useState(EMPTY_PROFILE_ENVELOPE);
  const [overview, setOverview] = useState(null);
  const [selectedEstablishmentId, setSelectedEstablishmentId] = useState('');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [editingProfile, setEditingProfile] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingReconciliation, setSavingReconciliation] = useState(false);
  const [acknowledgingRuleId, setAcknowledgingRuleId] = useState('');
  const [error, setError] = useState('');
  const [profileError, setProfileError] = useState('');
  const [reconciliationError, setReconciliationError] = useState('');
  const [reconciliationSuccess, setReconciliationSuccess] = useState('');
  const [configurationReloadKey, setConfigurationReloadKey] = useState(0);

  const profile = profileEnvelope?.profile || null;
  const establishments = useMemo(
    () => Array.isArray(profile?.establishments) ? profile.establishments : [],
    [profile]
  );
  const profileCountryAvailable = countries.some((country) => (
    country.code === profile?.country_code
    && (
      country.availability_status === 'available'
      || (!country.availability_status && country.code === 'SA')
    )
  ));
  const profileReady = isProfileReady(profileEnvelope) && profileCountryAvailable;
  const primaryEstablishmentId = useMemo(() => {
    const requestedId = profile?.primary_establishment_id;
    return establishments.some(({ id }) => id === requestedId)
      ? requestedId
      : establishments[0]?.id || '';
  }, [establishments, profile?.primary_establishment_id]);

  const loadOverview = useCallback(async ({ recalculate = false, signal } = {}) => {
    if (!companyUID || !profileReady) return null;
    const requestOptions = {
      forecastDays: 90,
      establishmentId: selectedEstablishmentId || primaryEstablishmentId,
      signal
    };

    try {
      const result = recalculate
        ? await nationalizationService.recalculate(companyUID, requestOptions)
        : await nationalizationService.getOverview(companyUID, requestOptions);
      setOverview(result);
      setError('');
      return result;
    } catch (requestError) {
      // A newly configured company may not have a calculation snapshot yet.
      // Ask the backend to create one once; legal calculations never run here.
      if (!recalculate && canManage && requestError?.status === 404) {
        const result = await nationalizationService.recalculate(companyUID, requestOptions);
        setOverview(result);
        setError('');
        return result;
      }
      throw requestError;
    }
  }, [canManage, companyUID, primaryEstablishmentId, profileReady, selectedEstablishmentId]);

  useEffect(() => {
    if (!companyUID) {
      setLoading(false);
      setError(t('errors.noCompany', 'Company could not be identified. Please sign in again.'));
      return undefined;
    }

    const controller = new AbortController();
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const [countryResult, profileResult] = await Promise.all([
          nationalizationService.getCountries({ signal: controller.signal }),
          nationalizationService.getProfile(companyUID, { signal: controller.signal }).catch((requestError) => {
            if (requestError?.status === 404) return EMPTY_PROFILE_ENVELOPE;
            throw requestError;
          })
        ]);
        if (controller.signal.aborted) return;

        const nextCountries = Array.isArray(countryResult?.countries) ? countryResult.countries : [];
        const nextProfileEnvelope = profileResult || EMPTY_PROFILE_ENVELOPE;
        const nextEstablishments = nextProfileEnvelope?.profile?.establishments || [];
        setCountries(nextCountries);
        setProfileEnvelope(nextProfileEnvelope);
        const requestedPrimaryId = nextProfileEnvelope?.profile?.primary_establishment_id;
        const nextPrimaryId = nextEstablishments.some(({ id }) => id === requestedPrimaryId)
          ? requestedPrimaryId
          : nextEstablishments[0]?.id || '';
        setSelectedEstablishmentId((current) => (
          nextEstablishments.some(({ id }) => id === current) ? current : nextPrimaryId
        ));
        setEditingProfile(!isProfileReady(nextProfileEnvelope));
      } catch (requestError) {
        if (requestError?.name !== 'AbortError') setError(getErrorMessage(requestError, t));
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    load();
    return () => controller.abort();
  }, [companyUID, configurationReloadKey, t]);

  useEffect(() => {
    if (!profileReady || editingProfile) return undefined;
    const controller = new AbortController();
    setLoading((value) => value || !overview);
    loadOverview({ signal: controller.signal })
      .catch((requestError) => {
        if (requestError?.name !== 'AbortError') setError(getErrorMessage(requestError, t));
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
    // `overview` is deliberately excluded: setting a result must not refetch it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingProfile, loadOverview, profileReady, t]);

  useEffect(() => {
    if (!profileReady) return undefined;
    const refreshFromWorkforceEvent = (event) => {
      if (event?.detail?.companyUID && event.detail.companyUID !== companyUID) return;
      setRefreshing(true);
      loadOverview({
        // View permission exposes aggregate snapshots only. Recalculation changes
        // server state and is therefore reserved for nationalization managers.
        recalculate: canManage && event?.detail?.recalculated !== true
      })
        .catch((requestError) => setError(getErrorMessage(requestError, t)))
        .finally(() => setRefreshing(false));
    };
    const refreshOnFocus = () => {
      loadOverview().catch(() => {
        // A focus refresh is opportunistic; keep the last valid snapshot visible.
      });
    };

    window.addEventListener(NATIONALIZATION_WORKFORCE_CHANGED_EVENT, refreshFromWorkforceEvent);
    window.addEventListener('focus', refreshOnFocus);
    return () => {
      window.removeEventListener(NATIONALIZATION_WORKFORCE_CHANGED_EVENT, refreshFromWorkforceEvent);
      window.removeEventListener('focus', refreshOnFocus);
    };
  }, [canManage, companyUID, loadOverview, profileReady, t]);

  const saveProfile = async (nextProfile) => {
    setSavingProfile(true);
    setProfileError('');
    try {
      const result = await nationalizationService.saveProfile(companyUID, nextProfile);
      const nextEnvelope = result?.profile ? result : { ...result, profile: nextProfile };
      setProfileEnvelope(nextEnvelope);
      const savedEstablishments = nextEnvelope?.profile?.establishments || [];
      const savedPrimaryId = nextEnvelope?.profile?.primary_establishment_id;
      setSelectedEstablishmentId(
        savedEstablishments.some(({ id }) => id === savedPrimaryId)
          ? savedPrimaryId
          : savedEstablishments[0]?.id || ''
      );
      setEditingProfile(false);
      setOverview(null);
    } catch (requestError) {
      setProfileError(getErrorMessage(requestError, t));
    } finally {
      setSavingProfile(false);
    }
  };

  const refresh = async () => {
    setRefreshing(true);
    try {
      await loadOverview({ recalculate: canManage });
    } catch (requestError) {
      setError(getErrorMessage(requestError, t));
    } finally {
      setRefreshing(false);
    }
  };

  const saveReconciliation = async (payload) => {
    setSavingReconciliation(true);
    setReconciliationError('');
    setReconciliationSuccess('');
    try {
      await nationalizationService.saveReconciliation(companyUID, payload);
      setReconciliationSuccess(t('notifications.reconciliationSaved', 'Official result saved.'));
      await loadOverview();
    } catch (requestError) {
      setReconciliationError(getErrorMessage(requestError, t));
    } finally {
      setSavingReconciliation(false);
    }
  };

  const acknowledgeRule = async (update, updateId) => {
    setAcknowledgingRuleId(updateId);
    try {
      await nationalizationService.acknowledgeRule(companyUID, update.rule_id, update.rule_version);
      setOverview((current) => ({
        ...current,
        rule_updates: (current?.rule_updates || []).map((item) => (
          item.rule_id === update.rule_id && item.rule_version === update.rule_version
            ? { ...item, acknowledged: true }
            : item
        ))
      }));
    } catch (requestError) {
      setError(getErrorMessage(requestError, t));
    } finally {
      setAcknowledgingRuleId('');
    }
  };

  if (loading && !overview && !editingProfile) {
    return <AppLoading inline label={t('common.loading', 'Loading...')} />;
  }

  if (!profileReady && countries.length === 0 && error) {
    return (
      <section className="nationalization-view nationalization-view--setup">
        <div className="nationalization-setup-unavailable" role="alert">
          <i className="fa-solid fa-plug-circle-xmark" aria-hidden="true" />
          <h2>{t('setup.serviceUnavailableTitle', 'Setup is not available yet')}</h2>
          <p>{uiText(error)}</p>
          <button
            type="button"
            className="nationalization-secondary-button"
            onClick={() => setConfigurationReloadKey((value) => value + 1)}
          >
            {t('actions.tryAgain', 'Try again')}
          </button>
        </div>
      </section>
    );
  }

  if (editingProfile || !profileReady) {
    return (
      <section className="nationalization-view nationalization-view--setup">
        {canManage ? (
          <NationalizationSetupForm
            countries={countries}
            initialProfile={profile}
            language={language}
            saving={savingProfile}
            error={profileError || error}
            t={t}
            onSave={saveProfile}
            onCancel={profileReady ? () => setEditingProfile(false) : null}
          />
        ) : (
          <div className="nationalization-support-card nationalization-centered-state">
            <i className="fa-solid fa-lock" aria-hidden="true" />
            <h3>{t('setup.adminRequiredTitle', 'Setup required')}</h3>
            <p>{t('setup.adminRequired', 'Ask a workspace administrator to complete the legal company profile before results can be calculated.')}</p>
          </div>
        )}
      </section>
    );
  }

  const programLabel = localizedValue(overview?.program, language)
    || t(`programs.${overview?.program?.code || profile?.country_code || 'nationalization'}`, 'Nationalization');
  const provisional = overview?.data_quality?.status_code && overview.data_quality.status_code !== 'complete';
  const coverageIncomplete = overview?.coverage?.status_code && overview.coverage.status_code !== 'complete';

  return (
    <section className="nationalization-view">
      <header className="nationalization-page-header">
        <div>
          <h2 className="nationalization-title">{t('page.title', 'Nationalization Overview')}</h2>
        </div>
        <div className="nationalization-header-actions">
          {establishments.length > 1 && (
            <label className="nationalization-establishment-selector">
              <span>{profile?.country_code === 'SA'
                ? t('page.qiwaEntity', 'Qiwa entity')
                : t('page.companyBranch', 'Company / branch')}</span>
              <select value={selectedEstablishmentId} onChange={(event) => setSelectedEstablishmentId(event.target.value)}>
                {establishments.map((establishment) => (
                  <option key={establishment.id} value={establishment.id}>{establishment.name || establishment.id}</option>
                ))}
              </select>
            </label>
          )}
        </div>
      </header>

      {error && (
        <div className="nationalization-inline-error" role="alert">
          <i className="fa-solid fa-circle-exclamation" aria-hidden="true" />
          <span>{uiText(error)}</span>
          <button type="button" onClick={refresh}>{t('actions.tryAgain', 'Try again')}</button>
        </div>
      )}

      {overview ? (
        <>
          <NationalizationOverviewCard
            overview={overview}
            programLabel={programLabel}
            language={language}
            t={t}
          >
            <NationalizationBreakdown overview={overview} language={language} t={t} />
          </NationalizationOverviewCard>
          <NationalizationRecommendations
            recommendations={overview?.recommendations}
            statusCode={overview?.current?.status_code}
            language={language}
            t={t}
          />
          {provisional && (
            <div className="nationalization-inline-warning">
              <i className="fa-solid fa-triangle-exclamation" aria-hidden="true" />
              <span>
                {coverageIncomplete
                  ? localizedValue(overview.coverage, language, 'limitation')
                    || t('resultStates.coverageIncomplete', 'Planning estimate — the reviewed rule catalogue is not complete yet. Verify the result with the regulator.')
                  : t('resultStates.provisional', 'Provisional result — some company or employee information is missing or unverified.')}
              </span>
            </div>
          )}
          <NationalizationCalculationChanges
            changes={overview?.calculation_changes}
            language={language}
            t={t}
          />
          <NationalizationRuleUpdates
            updates={overview?.rule_updates || overview?.notifications?.rule_updates}
            language={language}
            acknowledgingId={acknowledgingRuleId}
            t={t}
            onAcknowledge={canManage ? acknowledgeRule : null}
          />
          <NationalizationForecastCard overview={overview} language={language} t={t} />
          <div className="nationalization-support-grid">
            <NationalizationDataQuality
              dataQuality={overview?.data_quality}
              language={language}
              t={t}
              onOpenImport={canManage ? onOpenImport : null}
              onEditProfile={canManage ? () => setEditingProfile(true) : null}
              onRefresh={refresh}
              refreshing={refreshing}
            />
            <NationalizationRuleSources overview={overview} language={language} t={t} />
          </div>
          {canManage && (
            <NationalizationReconciliation
              establishments={establishments}
              obligations={overview?.obligations || []}
              language={language}
              latestReconciliation={overview?.latest_reconciliation}
              selectedEstablishmentId={selectedEstablishmentId}
              saving={savingReconciliation}
              error={reconciliationError}
              success={reconciliationSuccess}
              t={t}
              onSave={saveReconciliation}
            />
          )}
        </>
      ) : (
        <div className="nationalization-support-card nationalization-centered-state">
          <i className="fa-solid fa-calculator" aria-hidden="true" />
          <h3>{t('resultStates.noCalculationTitle', 'No calculation yet')}</h3>
          <p>{t('resultStates.noCalculation', 'Complete the workforce information, then ask JOBSEEQR to calculate the first result.')}</p>
          {canManage && (
            <button type="button" className="nationalization-primary-button" onClick={refresh} disabled={refreshing}>
              {t('actions.calculate', 'Calculate now')}
            </button>
          )}
        </div>
      )}
    </section>
  );
}
